import { useEffect, useState } from "react";
import axios from "axios";
import { Clock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function NewsPage() {
  const [news, setNews] = useState([]);

  useEffect(() => {
    axios.get(`${API}/news`).then(r => setNews(r.data)).catch(() => {});
  }, []);

  const featured = news.find(n => n.is_featured);
  const rest = news.filter(n => n.id !== featured?.id);
  const today = new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });

  return (
    <div data-testid="news-page" className="bg-[#F0E8D0] min-h-screen">
      {/* Newspaper masthead */}
      <section className="pt-8 pb-4 px-4">
        <div className="max-w-5xl mx-auto text-center">
          {/* Top rule */}
          <div className="h-[2px] bg-foreground mb-3" />
          <div className="flex items-center justify-between mb-1">
            <p className="font-body text-[10px] text-foreground/50 uppercase tracking-[0.3em]">Est. 2026</p>
            <p className="font-body text-[10px] text-foreground/50 uppercase tracking-[0.3em]">{today}</p>
            <p className="font-body text-[10px] text-foreground/50 uppercase tracking-[0.3em]">Piedmont Region</p>
          </div>
          <div className="h-[1px] bg-foreground/30 mb-4" />

          {/* Masthead title */}
          <h1
            data-testid="news-title"
            className="font-heading text-5xl sm:text-6xl md:text-7xl uppercase leading-none tracking-tight mb-1"
            style={{ letterSpacing: '-0.02em' }}
          >
            The Piedmont Gazette
          </h1>
          <p className="font-body text-xs text-foreground/50 uppercase tracking-[0.4em] mb-2">
            Official Newspaper of the Piedmont Wrestling Club
          </p>

          {/* Double rule */}
          <div className="h-[3px] bg-foreground mb-[2px]" />
          <div className="h-[1px] bg-foreground" />
        </div>
      </section>

      {/* Newspaper body */}
      <section className="pb-16 px-4">
        <div className="max-w-5xl mx-auto">
          {news.length === 0 ? (
            <div className="py-24 text-center">
              <div className="h-[1px] bg-foreground/20 mb-8" />
              <Clock size={28} className="mx-auto text-foreground/30 mb-4" />
              <p className="font-heading text-xl text-foreground/40 uppercase">No Dispatches at This Time</p>
              <p className="font-body text-sm text-foreground/30 mt-2">Check back soon for the latest from the territory.</p>
              <div className="h-[1px] bg-foreground/20 mt-8" />
            </div>
          ) : (
            <>
              {/* Featured / lead story */}
              {featured && (
                <div data-testid={`news-featured-${featured.id}`} className="pt-6 pb-8">
                  <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
                    <div className={featured.image_url ? "md:col-span-7" : "md:col-span-12"}>
                      {/* Headline */}
                      <h2
                        className="font-heading text-3xl sm:text-4xl md:text-5xl uppercase leading-none mb-4"
                        style={{ letterSpacing: '-0.01em' }}
                      >
                        {featured.title}
                      </h2>
                      <div className="h-[2px] bg-foreground mb-3 w-20" />
                      <p className="font-body text-[10px] text-foreground/40 uppercase tracking-[0.3em] mb-4">
                        Published {new Date(featured.created_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                      </p>
                      {/* Body in newspaper columns */}
                      <div
                        className="font-body text-sm leading-relaxed text-foreground/80 whitespace-pre-line"
                        style={{
                          columnCount: featured.body.length > 400 ? 2 : 1,
                          columnGap: '2rem',
                          columnRule: '1px solid rgba(0,0,0,0.12)',
                        }}
                      >
                        <span className="font-heading text-3xl float-left mr-1 mt-0 leading-none">
                          {featured.body.charAt(0)}
                        </span>
                        {featured.body.substring(1)}
                      </div>
                    </div>
                    {featured.image_url && (
                      <div className="md:col-span-5">
                        <img
                          src={featured.image_url}
                          alt={featured.title}
                          className="w-full h-auto object-cover vintage-img"
                        />
                        <p className="font-body text-[9px] text-foreground/30 uppercase tracking-wider mt-1 text-center italic">
                          Photo — Piedmont Wrestling Club
                        </p>
                      </div>
                    )}
                  </div>
                  {/* Section rule */}
                  <div className="mt-8 h-[3px] bg-foreground mb-[2px]" />
                  <div className="h-[1px] bg-foreground" />
                </div>
              )}

              {/* Remaining articles — newspaper column layout */}
              {rest.length > 0 && (
                <div
                  className="pt-6"
                  style={{
                    columnCount: rest.length >= 2 ? (window.innerWidth >= 768 ? 2 : 1) : 1,
                    columnGap: '2.5rem',
                    columnRule: '1px solid rgba(0,0,0,0.15)',
                  }}
                >
                  {rest.map((article) => (
                    <div
                      key={article.id}
                      data-testid={`news-article-${article.id}`}
                      className="mb-8"
                      style={{ breakInside: 'avoid' }}
                    >
                      {article.image_url && (
                        <div className="mb-3">
                          <img
                            src={article.image_url}
                            alt={article.title}
                            className="w-full h-48 object-cover vintage-img"
                          />
                        </div>
                      )}
                      <h3
                        className="font-heading text-xl sm:text-2xl uppercase leading-tight mb-2"
                      >
                        {article.title}
                      </h3>
                      <div className="h-[1px] bg-foreground/20 mb-2 w-12" />
                      <p className="font-body text-[10px] text-foreground/40 uppercase tracking-[0.3em] mb-3">
                        {new Date(article.created_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                      </p>
                      <div className="font-body text-sm leading-relaxed text-foreground/70 whitespace-pre-line">
                        {article.body.length > 250
                          ? article.body.substring(0, 250) + '...'
                          : article.body}
                      </div>
                      <div className="h-[1px] bg-foreground/15 mt-6" />
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {/* Newspaper footer */}
          <div className="mt-8 text-center">
            <div className="h-[1px] bg-foreground/20 mb-4" />
            <p className="font-body text-[9px] text-foreground/30 uppercase tracking-[0.4em]">
              The Piedmont Gazette &mdash; Return to Tradition &mdash; &copy; {new Date().getFullYear()} Piedmont Wrestling Club
            </p>
            <div className="h-[1px] bg-foreground/20 mt-4" />
          </div>
        </div>
      </section>
    </div>
  );
}
